import { toast } from 'react-toastify';
import http from '../../../Utils/http';
import responseMessageHandler from '../../../Utils/ResponseMessageHandler';
import { normalizeLayoutType, mapLayoutTypeNameToCode } from '../../../Utils/layoutTypeUtils';

const LayoutConfigurations = {
    name: 'layoutConfigurations',
    state: {
        data: {},
        loading: false,
        selectedId: null,
        error: '',
    },
    reducers: {
        createRequested(state) {
            return {
                ...state,
                loading: true,
                error: '',
            };
        },

        createSucceeded(state, payload) {
            let data = [];
            if (payload?.data && Array.isArray(payload.data)) {
                data = payload.data.map((item) => {
                    const rawContainer = item.configurations && !Array.isArray(item.configurations)
                        ? item.configurations
                        : null;
                    const configurations = Array.isArray(rawContainer?.configurations)
                        ? rawContainer.configurations
                        : (Array.isArray(item.configurations) ? item.configurations : item);

                    const layoutSource = rawContainer || item;
                    const layoutType = normalizeLayoutType(layoutSource);
                    const layout_type =
                        typeof layoutSource.layout_type === 'number'
                            ? layoutSource.layout_type
                            : mapLayoutTypeNameToCode(layoutType);

                    const baseWidth =
                        rawContainer?.baseWidth ||
                        rawContainer?.base_width ||
                        item.baseWidth ||
                        item.base_width ||
                        1920;
                    const baseHeight =
                        rawContainer?.baseHeight ||
                        rawContainer?.base_height ||
                        item.baseHeight ||
                        item.base_height ||
                        1080;

                    const orientation =
                        rawContainer?.orientation ||
                        item.orientation ||
                        item.layoutOrientation ||
                        'Landscape';

                    const name = item.layout_name || '';

                    return {
                        id: item.id,
                        configurations,
                        layoutType,
                        layout_type,
                        orientation,
                        baseWidth,
                        baseHeight,
                        name,
                    };
                });
            }
            return {
                ...state,
                data: data,
                loading: false,
                error: '',
            };
        },

        setSelectedId(state, payload) {
            return {
                ...state,
                selectedId: payload?.id,
                loading: false,
                error: '',
            };
        },
        createFailed(state, payload) {
            return {
                ...state,
                error: payload,
                loading: false,
                selectedId: null,
            };
        },
        clearStats(state) {
            return {
                ...state,
                selectedId: null,
            };
        },
        updateRequested(state) {
            return {
                ...state,
                loading: true,
                error: '',
            };
        },
        updateSucceeded(state) {
            return {
                ...state,
                loading: false,
                error: '',
            };
        },
        updateFailed(state, payload) {
            return {
                ...state,
                error: payload,
                loading: false,
            };
        },
    },
    effects: () => ({
        async createLayoutConfiguration({
            configurations,
            layoutType,
            layout_type,
            orientation,
            baseWidth,
            baseHeight,
            name,
        }) {
            try {
                this.createRequested();
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                const payload = {
                    configurations,
                    user_id: userInfo?.id,
                    org_id: userInfo?.org_id,
                    layoutType,
                    layout_type: layout_type || mapLayoutTypeNameToCode(layoutType),
                    orientation,
                    baseWidth,
                    baseHeight,
                    name,
                };
                const response = await http.post('savvy/layout-configurations', payload);
                const responseData = response?.data;
                const createdCandidate = responseData?.data ?? responseData;
                const createdId = Array.isArray(createdCandidate)
                    ? createdCandidate?.[0]?.id
                    : createdCandidate?.id ?? responseData?.id ?? null;

                if (createdId !== null && createdId !== undefined) {
                    this.setSelectedId({ id: createdId });
                }

                toast.success('Layout configuration created successfully!');

                return createdId;
            } catch (error) {
                this.createFailed(error.message);
                toast.error(responseMessageHandler.show(error.message));
            }
        },
        async getAllLayouts() {
            try {
                this.createRequested();
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                const response = await http.get(`savvy/layout-configurations/?user_id=${userInfo?.id}`);
                this.createSucceeded(response.data);
            } catch (error) {
                this.createFailed(error.message);
                toast.error(responseMessageHandler.show(error.message));
            }
        },
        async clearLayoutStats() {
            this.clearStats();
        },
        async updateLayoutConfiguration({
            id,
            name,
            zones,
            confirm_impact,
        }) {
            try {
                this.updateRequested();
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                const payload = {
                    org_id: userInfo?.org_id,
                    user_id: userInfo?.id,
                    name,
                    zones,
                    confirm_impact: confirm_impact || false,
                };
                const response = await http.put(`savvy/layout-configurations/${id}`, payload);
                this.updateSucceeded();

                toast.success('Layout configuration updated successfully!');

                return response?.data?.data?.layout ?? { id };
            } catch (error) {
                if (error?.response?.status === 409) {
                    this.updateFailed('');
                    return { warning: error.response.data?.warning };
                }
                this.updateFailed(error.message);
                toast.error(responseMessageHandler.show(error.message));
            }
        },
        async deleteLayoutConfiguration({ id, confirm_impact }) {
            try {
                this.updateRequested();
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                const params = new URLSearchParams({
                    org_id: userInfo?.org_id ?? '',
                    user_id: userInfo?.id ?? '',
                    confirm_impact: confirm_impact ? 'true' : 'false',
                });
                await http.delete(`savvy/layout-configurations/${id}?${params.toString()}`);
                this.updateSucceeded();

                toast.success('Layout deleted successfully!');

                return { success: true };
            } catch (error) {
                if (error?.response?.status === 409) {
                    this.updateFailed('');
                    const data = error.response.data;
                    if (data?.warning) {
                        return { warning: data.warning };
                    }
                    toast.error(data?.message || 'This layout cannot be deleted.');
                    return { blocked: true, message: data?.message, displaysCount: data?.displays_count };
                }
                this.updateFailed(error.message);
                toast.error(responseMessageHandler.show(error.message));
            }
        },
    }),
};

export default LayoutConfigurations;
